// src/pages/AdsStart/AdsStart.jsx
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { sendToApp, addAppMessageListener } from "../../bridges/appBridge";

const AdsStart = () => {
  const navigate = useNavigate();

  const [installId, setInstallId] = useState(undefined);
  const [deviceToken, setDeviceToken] = useState(undefined);

  const navigatedOnce = useRef(false);
  const scheduleNavigate = (path, opts) => {
    if (navigatedOnce.current) return;
    navigatedOnce.current = true;
    // ⛔ 여기서는 WEB_LOADING_DONE 안 보냄 (AdsMain에서만 보냄)
    navigate(path, opts || { replace: true });
  };

  // === (이하 기존 로직 동일) ===
  const gotInstallIdRef = useRef(false);
  const gotDeviceTokenRef = useRef(false);

  useEffect(() => {
    const cachedInstall = localStorage.getItem("install_id") || "";
    if (cachedInstall && installId === undefined) setInstallId(cachedInstall);

    const cachedToken = localStorage.getItem("device_token") || "";
    if (cachedToken && deviceToken === undefined) setDeviceToken(cachedToken);

    let toInstall, toPush;

    const off = addAppMessageListener((msg) => {
      const t = msg?.type;
      const p = msg?.payload || {};

      if (t === "WEB_READY_ACK" || t === "INSTALLATION_ID") {
        const iid = p.install_id || localStorage.getItem("install_id") || "";
        if (iid) {
          localStorage.setItem("install_id", iid);
          setInstallId(iid);
          gotInstallIdRef.current = true;
          if (toInstall) clearTimeout(toInstall);
        }
      }

      if (t === "PUSH_TOKEN" && p.token) {
        localStorage.setItem("platform", p.platform || "unknown");
        localStorage.setItem("device_token", p.token || null);
        setDeviceToken(p.token || null);
        // alert(p.token)
        gotDeviceTokenRef.current = true;
        if (toPush) clearTimeout(toPush);
      }
      if (t === "PUSH_NAVIGATE") {
        const route = p.route;

        if (route) {
          localStorage.setItem(
            "pending_push",
            JSON.stringify({
              action: p.action || "navigate",
              route,
              source: p.source || "unknown",
              at: p.at || Date.now(),
            })
          );
        }
      }

    });

    sendToApp("WEB_READY", { ts: Date.now() });
    sendToApp("GET_INSTALLATION_ID", { ts: Date.now() });
    sendToApp("GET_PUSH_TOKEN", { ts: Date.now() });

    toInstall = setTimeout(() => {
      if (!gotInstallIdRef.current) {
        const again = localStorage.getItem("install_id") || "";
        setInstallId(again || null);
      }
    }, 3000);

    toPush = setTimeout(() => {
      if (!gotDeviceTokenRef.current) {
        const again = localStorage.getItem("device_token") || "";
        setDeviceToken(again || null);
      }
    }, 3000);

    return () => {
      off?.();
      clearTimeout(toInstall);
      clearTimeout(toPush);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // 아직 수신 대기
    if (installId === undefined || deviceToken === undefined) return;

    // 하나라도 null → 권한 페이지 (2초 보장 이동)
    if (installId === null || deviceToken === null) {
      scheduleNavigate("/ads/permission", { replace: true });
      return;
    }

    // 둘 다 truthy여야 다음
    if (!installId || !deviceToken) return;

    localStorage.setItem("install_id", installId);
    localStorage.setItem("device_token", deviceToken);

    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/install/check`,
          { params: { install_id: installId } }
        );
        const exists = !!data?.exists;

        if (!exists) {
          scheduleNavigate("/ads/permission", { replace: true });
          return;
        }

        try {
          const jwt = localStorage.getItem("access_token");
          const deviceToken = localStorage.getItem("device_token");
          if (!jwt) throw new Error("no token");

          const res = await axios.post(
            `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/auto/login`,
            { access_token: jwt, device_token: deviceToken },
            { headers: { "Content-Type": "application/json" } }
          );

          axios.defaults.headers.common["Authorization"] = `Bearer ${jwt}`;
          localStorage.setItem("user_id", String(res.data.user_id ?? ""));
          if (res.data.store_business_number != null) {
            localStorage.setItem(
              "store_business_number",
              String(res.data.store_business_number)
            );
          } else {
            localStorage.removeItem("store_business_number");
          }

          const { status, store_business_number: storeBusinessNumber } = res.data;

          if (status === "active") {
            scheduleNavigate("/ads/main", {
              replace: true,
              state: { storeBusinessNumber },
            });
          } else if (status === "logout" || status === "auto_off") {
            scheduleNavigate("/ads/register?step=1&auth=login", { replace: true, installId });
          } else if (status === "approved") {
            scheduleNavigate("/ads/register?step=2", { replace: true });
          } else if (status === "agreed") {
            scheduleNavigate("/ads/register?step=3", { replace: true });
          } else if (status === "verified") {
            scheduleNavigate("/ads/register?step=4", { replace: true });
          } else {
            scheduleNavigate("/ads/register", { replace: true });
          }
        } catch {
          try { delete axios.defaults.headers.common["Authorization"]; } catch { }
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          localStorage.removeItem("user_id");
          localStorage.removeItem("store_business_number");
          scheduleNavigate("/ads/register", { replace: true });
        }
      } catch {
        scheduleNavigate("/ads/permission", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installId, deviceToken]);


};

export default AdsStart;
